import { useState, useEffect } from "react";
import { Row, Col, Modal, Button, message, Spin } from "antd";
import { withRouter } from "react-router";
import { LeftCol, RightCol } from "./components";
import {
  getCopilotById,
  updateCopilotById,
  uploadCopilotImage,
} from "@api/copilots/browser-extension/copilot/copilot";
import { SuccessNotification, ErrorNotification } from "../..";
import { getAllCategories } from "@api/copilots/browser-extension/copilot/category";
import { getAllPlans } from "@api/subscription/plan";

const initialState = {
  image: "",
  name: "",
  command: "",
  description: "",
  category: "",
  order: 0,
  plans: [],
  quickAccess: false,
  model: "",
  temperature: "",
  max_tokens: "",
  top_p: 1,
  frequency_penalty: 0,
  presence_penalty: 0,
  inputs: [],
  publish: false,
};

const CopilotUpdateModal = ({
  open,
  handleModalClick,
  fetchCopilots,
  copilotId,
}) => {
  const [values, setValues] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [plans, setPlans] = useState([]);

  // Fetch all categories
  const fetchCategoriesPlansCopilots = async () => {
    setLoading(true);
    const catRes = await getAllCategories();
    setCategories(catRes.data);

    const planRes = await getAllPlans();
    // API returns { plans: [...], pagination: {...} }
    setPlans(planRes.data.plans || planRes.data || []);

    getCopilotById(copilotId).then((res) => {
      setValues(res.data);
    });
    setLoading(false);
  };

  useEffect(() => {
    fetchCategoriesPlansCopilots();
  }, []);

  // Handle form change
  const handleFormChange = (e) => {
    e.preventDefault();
    setValues({
      ...values,
      [e.target.name]: e.target.value,
    });
  };

  // Handle copilot update
  const handleCopilotUpdate = async (e) => {
    setLoading(true);
    e.preventDefault();

    // Validate form
    if (!values.name) {
      setLoading(false);
      return ErrorNotification("Please enter a name");
    }

    // Update copilot
    updateCopilotById(copilotId, values)
      .then(() => {
        setValues(initialState);
        handleModalClick();
        fetchCopilots();
        SuccessNotification("Copilot updated successfully");
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        ErrorNotification(err.response.data.message);
      });
  };

  // Before image upload
  const beforeImageUpload = (file) => {
    // Image can be jpeg, png, or gif
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
    if (!isJpgOrPng) {
      message.error("You can only upload JPEG/PNG files!");
    }
    const isLt2M = file.size / 1024 / 1024 < 3;
    if (!isLt2M) {
      message.error("Image must smaller than 3MB!");
    }
    return isJpgOrPng && isLt2M;
  };

  // Handle image upload
  const handleUploadImage = async (options) => {
    const { onError, file } = options;
    const fmData = new FormData();

    const config = {
      headers: { "content-type": "multipart/form-data" },
    };

    fmData.append("image", file);

    // Make request to upload image
    try {
      setLoading(true);
      const res = await uploadCopilotImage(fmData, config);
      setValues({
        ...values,
        image: res.data.url,
      });
      setLoading(false);
    } catch (error) {
      onError(error);
      setLoading(false);
    }
  };

  return (
    <>
      <Modal
        title={
          <Row justify="center">
            <Col span={24}>
              <h1 className="h2">Update Copilot</h1>
            </Col>
          </Row>
        }
        visible={open}
        onOk={handleModalClick}
        closable={false}
        width={1000}
        centered
        style={{
          top: "0.5rem",
        }}
        footer={[
          <Button key="back" onClick={handleModalClick}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={handleCopilotUpdate}
            loading={loading}
          >
            Update
          </Button>,
        ]}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "calc(80vh - 50px)",
            }}
          >
            <Spin />
          </div>
        ) : (
          <Row justify="space-between">
            <Col span={13}>
              <LeftCol
                loading={loading}
                values={values}
                setValues={setValues}
                handleFormChange={handleFormChange}
                categories={categories}
                plans={plans}
                handleUploadImage={handleUploadImage}
                beforeImageUpload={beforeImageUpload}
              />
            </Col>

            <Col span={11}>
              <RightCol
                loading={loading}
                values={values}
                setValues={setValues}
                handleFormChange={handleFormChange}
              />
            </Col>
          </Row>
        )}
      </Modal>
    </>
  );
};

export default withRouter(CopilotUpdateModal);
